/** The source language. Every other language must define exactly these keys (the types enforce it). */
export const enUS = {
    // app bar and shell
    'app.name': 'Arcana',
    'app.tagline': 'Tarot for the questions you keep to yourself',
    'app.support': 'Support',
    'app.supportAria': 'Support Arcana',
    'app.language': 'Language',
    'app.loading': 'Loading',
    'app.loadError':
        'Could not load this language. Check your connection and try again.',
    'app.retry': 'Try again',
    'app.settings': 'Settings',
    'app.sound': 'Sound and vibration',
    'reminders.title': 'Reminders',
    'reminders.morning.title': 'Your card of the day awaits',
    'reminders.morning.body':
        'You have not drawn today. Take a quiet moment and let the cards speak.',
    'reminders.midnight.title': 'A new reading is ready',
    'reminders.midnight.body': 'A new day has begun. Come and draw your card.',
    'reminders.on': 'Reminders are on.',
    'reminders.offerTitle': 'Daily reminders?',
    'reminders.offerBody':
        'We can nudge you in the morning if you have not drawn your card, and at midnight when a new reading is ready.',
    'reminders.notNow': 'Not now',
    'reminders.channel': 'Daily reminders',
    'reminders.denied':
        'Notifications are blocked. Allow them in your device settings to get reminders.',
    'reminders.unsupported': 'This browser cannot show notifications.',
    'reminders.offer': 'Remind me daily',
    'reminders.webNote':
        'In the browser, reminders arrive while Arcana is open. The mobile app delivers them any time.',
    'app.back': 'Back',
    'app.close': 'Close',
    'app.backHome': '← Back to Arcana',
    'app.getOnMobile':
        'Take your readings everywhere, with vibration and your history kept on your phone.',
    'app.getOnMobileButton': 'Get on mobile',
    'app.getOnMobileTitle': 'Get {name} on your phone',
    'store.googlePlay': 'Get it on Google Play',
    'store.appStore': 'Get it on the App Store',
    'store.comingSoon': 'Coming soon',
    'store.comingSoonGooglePlay': 'Coming soon to Google Play',
    'store.comingSoonAppStore': 'Coming soon to the App Store',

    // home
    'home.daily.title': 'Card of the day',
    'home.daily.intro':
        'One card, once a day. Ask what you most need to hear, then let the deck answer.',
    'home.daily.draw': "Draw today's card",
    'home.daily.sealed':
        'Your card is drawn and waiting, face down. A short video turns it over.',
    'home.daily.unlock': "Reveal today's card",
    'home.daily.view': 'Read it again',
    'home.daily.next': 'The deck rests. Next card in {time}',
    'home.daily.sealedFree':
        'Your card is drawn and waiting, face down. Tap to turn it over.',
    'home.spreads.title': 'Readings',
    'home.spreads.note': 'A short video opens each reading.',
    'home.spreads.noteFree': 'Each reading opens at once, with no ads.',
    'home.history': 'Your readings',
    'home.breathe': 'Breathe',
    'breathe.title': 'A moment to breathe',
    'breathe.intro':
        'Pick a rhythm and follow the orb. Nothing is saved or sent: it is just a pause.',
    'breathe.pattern.box': 'Box · 4-4-4-4',
    'breathe.pattern.478': '4-7-8',
    'breathe.patternAria': 'Breathing pattern',
    'breathe.start': 'Begin',
    'breathe.stop': 'Stop',
    'breathe.ready': 'Ready when you are',
    'breathe.inhale': 'Breathe in',
    'breathe.hold': 'Hold',
    'breathe.exhale': 'Breathe out',
    'breathe.rest': 'Pause',
    'breathe.note':
        'A calm pause, not a treatment. Go at your own pace, and stop whenever you like.',

    // spreads
    'spread.daily.name': 'Card of the day',
    'spread.love.name': 'Love reading',
    'spread.love.desc': 'You, them, what lies between you, and what to do.',
    'spread.work.name': 'Work reading',
    'spread.work.desc': 'Your situation, the obstacle, and the way through.',
    'spread.path.name': 'Past, present, future',
    'spread.path.desc':
        'Where you came from, where you stand, what approaches.',
    'spread.yesno.name': 'Yes or no',
    'spread.yesno.desc': 'One question, one card, a straight answer.',
    'pos.today': 'Today',
    'pos.you': 'You',
    'pos.them': '{name}',
    'pos.bond': 'Between you',
    'pos.guidance': 'Guidance',
    'pos.situation': 'The situation',
    'pos.obstacle': 'The obstacle',
    'pos.advice': 'The way through',
    'pos.past': 'Past',
    'pos.present': 'Present',
    'pos.future': 'Future',
    'pos.answer': 'The answer',
    'lead.you': 'Here is how this card speaks of you:',
    'lead.them': 'Here is how this card speaks of {name}:',
    'lead.bond': 'Here is how this card speaks of what lies between you:',
    'lead.guidance': 'Here is what this card invites you to reflect on:',
    'lead.situation': 'Here is how this card describes where you stand:',
    'lead.obstacle': 'Here is how this card describes what stands in your way:',
    'lead.advice': 'Here is what this card invites you to consider:',
    'lead.past': 'Looking back, this card describes what shaped you:',
    'lead.present': 'This card describes what surrounds you right now:',
    'lead.future': 'This card describes what is taking shape:',
    'lead.answer': 'Here is why the cards answer this way:',
    'lead.you.rev':
        'Reversed, this card speaks of a side of you that is held back or turned inward:',
    'lead.them.rev':
        'Reversed, this card speaks of a side of {name} that is held back or turned inward:',
    'lead.bond.rev':
        'Reversed, this card speaks of what is blocked or unspoken between you:',
    'lead.guidance.rev':
        'Reversed, this card invites you to look first at what is getting in the way:',
    'lead.situation.rev':
        'Reversed, this card describes a situation where something is stuck or out of balance:',
    'lead.obstacle.rev':
        'Reversed, this card describes an obstacle that may be more inner than outer:',
    'lead.advice.rev':
        'Reversed, this card invites you to consider what needs to loosen first:',
    'lead.past.rev':
        'Looking back, reversed, this card describes something that was held back or left unfinished:',
    'lead.present.rev':
        'Reversed, this card describes what feels blocked or unresolved right now:',
    'lead.future.rev':
        'Reversed, this card describes a tendency that may stall unless it is looked at:',
    'lead.answer.rev':
        'Reversed, the card turns its meaning around. Here is why the cards answer this way:',

    // ritual
    'ritual.name.title': 'Who is this reading about?',
    'ritual.name.hint':
        'A first name or a nickname. It stays on your phone and is only used to address the cards.',
    'ritual.name.placeholder': 'Their name',
    'ritual.name.titleDaily': 'What should the cards call you?',
    'ritual.name.hintDaily':
        'A first name or a nickname. You only say it once, it stays on your phone, and it lets the card of the day speak to you.',
    'ritual.name.placeholderDaily': 'Your name',
    'ritual.name.skip': 'I would rather not say',
    'ritual.continue': 'Continue',
    'ritual.reflect.title': 'Breathe. Hold your question.',
    'ritual.reflect.titleLove': 'Close your eyes. Think of {name}.',
    'ritual.reflect.body.daily':
        'Let the noise of the day settle. Ask, without words if you can: what do I need to know today?',
    'ritual.reflect.body.love':
        'Let their face, their voice and the way things stand between you rise in your mind. Do not ask for a result. Ask to understand.',
    'ritual.reflect.body.work':
        'Bring to mind the work, the people and the pressure. Ask what you cannot yet see from inside it.',
    'ritual.reflect.body.path':
        'Think of the road behind you and the one ahead. Ask what connects them.',
    'ritual.reflect.body.yesno':
        'Hold one question, clear and honest, that can be answered yes or no. Ask it only once.',
    'ritual.breathIn': 'Inspire',
    'ritual.breathHold': 'Hold',
    'ritual.breathOut': 'Release',
    'ritual.ready': 'I am ready',
    'ritual.begin.title': 'The deck is listening',
    'ritual.begin.tap': 'Touch the deck to begin',
    'ritual.charging': 'The cards are choosing you…',
    'ritual.choose.title': 'Choose your cards',
    'ritual.choose.hintOne':
        'Trust your hand. Touch the one card that calls to you.',
    'ritual.choose.hintMany':
        'Trust your hand. Touch {count} cards, one for each position below. Touch a chosen card again to put it back.',
    'ritual.choose.card': 'Card {n}',
    'ritual.choose.confirm': 'These are my cards',
    'ritual.drawn.title': 'Your cards are drawn',
    'ritual.drawn.body':
        'They lie face down, already decided. A short video turns them over.',
    'ritual.drawn.bodyFree':
        'They lie face down, already decided. Turn them over when you are ready.',
    'ritual.reveal': 'Watch a video to reveal',
    'ritual.revealFree': 'Reveal',
    'ritual.opening': 'Opening…',
    'ritual.dismissed':
        'The video was closed early. Watch it to the end to reveal your cards.',
    'ritual.abandon': 'Leave the ritual',

    // reading
    'reading.for': 'For {name}',
    'reading.tapToTurn': 'Touch each card to turn it',
    'reading.turnAll': 'Turn all',
    'reading.reversed': 'Reversed',
    'reading.upright': 'Upright',
    'reading.advice': 'Advice',
    'reading.dailyAdvice': "Today's advice",
    'reading.verdict.yes': 'Yes',
    'reading.verdict.no': 'No',
    'reading.verdict.maybe': 'Not yet',
    'reading.overallTitle': 'The spread as a whole',
    'overall.fated':
        'Many major arcana and few reversals: this is a weighty moment, and the cards speak plainly.',
    'overall.open':
        'Every card stands upright: the energy here is open and moves freely. Act on it.',
    'overall.mixed':
        'Some cards upright, some reversed: there is movement and friction together. Attend to the reversed cards first.',
    'overall.blocked':
        'Most cards are reversed: something is held back, hidden or resisted. Slow down and look inward before you act.',
    'summary.thread.love':
        'You come to this reading under “{c0}”, and {name} appears as “{c1}”. What lives between you is “{c2}”, and the guidance of the cards comes from “{c3}”. Read the four as one story about the same bond: each card shows a side of it, and the meaning of the reading is in the conversation between them.',
    'summary.thread.work':
        'The situation is “{c0}”, the obstacle appears as “{c1}” and the suggested way is “{c2}”. Notice how the third card answers the second: the advice is usually the direct reply to what holds your step back.',
    'summary.thread.path':
        'The past brings “{c0}”, the present lives under “{c1}” and the future points to “{c2}”. The future here is a tendency, not a sentence: it changes with what you do with the present. Look for the thread through all three cards, the theme that returns in a different coat.',
    'summary.yesno.yes':
        'The answer is yes, and it comes from “{c0}”. A yes is not a promise: it shows where the wind is favourable, and you are still the one at the helm. You may want to ask yourself what you would need in order to say this yes wholeheartedly.',
    'summary.yesno.no':
        'The answer is no for now, and it comes from “{c0}”. A no is not a verdict on you: it points at what does not fit as it stands. You may want to ask yourself what this no is protecting, and which question would serve you better next time.',
    'summary.yesno.maybe':
        'The cards do not settle it yet, and “{c0}” shows why. A not-yet often points at something unripe or still unseen. You may want to ask yourself what you would need to know in order to answer this on your own.',
    'summary.suit.wands':
        'The suit of Wands dominates this reading: the subject is energy, desire and action. The question behind it all is where you want to put your fire.',
    'summary.suit.cups':
        'The suit of Cups dominates this reading: the subject is feeling. Emotion, connection and intuition weigh more here than any argument.',
    'summary.suit.swords':
        'The suit of Swords dominates this reading: the subject lives in the mind, in thoughts, words and decisions. Separate what is fact from the story you tell.',
    'summary.suit.pentacles':
        'The suit of Pentacles dominates this reading: the subject is concrete, made of time, money, body and routine. What counts is what is built slowly and can be touched.',
    'summary.reversed.one':
        'The reversed card, {list}, is the point of attention: what it asks for is usually held back, postponed or hidden. Start with it.',
    'summary.reversed.many':
        'The reversed cards ({list}) are the points of attention: what they ask for is usually held back, postponed or hidden. Start with them.',
    'reading.ask': 'To sit with',
    'reading.reflect': 'Ask yourself',
    'reading.saved': 'Saved to your readings',
    'reading.shareLinkCopied':
        'Link copied. In Stories, add it with the Link sticker',
    'reading.share': 'Share',
    'reading.shareFailed': 'Could not create the picture',
    'reading.shareDownloaded': 'Picture saved',
    'reading.shareMessage': 'My card of the day in Arcana',
    'reading.disclaimer':
        'For reflection and entertainment. A reading is a mirror, not a prediction, and no substitute for professional advice.',
    'reading.done': 'Close the reading',
    'reading.notFound': 'This reading is no longer saved.',

    // history
    'history.title': 'Your readings',
    'history.empty':
        'Nothing here yet. Your readings are kept on this phone once you open them.',
    'history.forName': 'for {name}',
    'history.deleteTitle': 'Delete this reading?',
    'history.deleteBody':
        'It is removed from this phone and cannot be restored.',
    'history.cancel': 'Cancel',
    'history.delete': 'Delete',

    // reflection questions
    'prompt.general.0':
        'What would change if you trusted this card completely?',
    'prompt.general.1':
        'Which part of this reading did you want to argue with, and why?',
    'prompt.general.2':
        'What is one small action that would honor this message today?',
    'prompt.general.3': 'What are you hoping the cards will not say?',
    'prompt.general.4': 'Where in your life does this energy already live?',
    'prompt.general.5':
        'If this were advice from your wisest self, what would you do next?',
    'prompt.love.0':
        'What do you truly need from this bond, and have you said it aloud?',
    'prompt.love.1':
        'What part of this pattern belongs to you, and what belongs to them?',
    'prompt.love.2': 'If nothing about them changed, what would you choose?',
    'prompt.love.3': 'What are you afraid will happen if you tell the truth?',
    'prompt.love.4':
        'What did this love teach you about how you wish to be loved?',
    'prompt.love.5':
        'What would tenderness look like toward yourself and toward them this week?',
    'prompt.work.0':
        'What would you do here if you were not afraid of being judged?',
    'prompt.work.1': 'Which obstacle is real, and which is a story you tell?',
    'prompt.work.2':
        'What would a year of this path look like, and do you want it?',
    'prompt.work.3': 'Who could help you, and what stops you from asking?',
    'prompt.work.4':
        'What is the smallest next step you could take before the week ends?',
    'prompt.work.5': 'What are you tolerating that you have already outgrown?',

    // ads
    'ads.advertisement': 'Advertisement',
    'ads.footerPreview': 'Footer ad preview',
    'ads.breakTitle': 'A short pause · keeps Arcana free',
    'ads.breakPreview': 'Ad break preview',
    'ads.continue': 'Reveal',
    'ads.continueIn': 'Reveal in {seconds}s',
    'footer.about': 'About',
    'footer.privacy': 'Privacy',

    // support
    'support.title': 'Support Arcana',
    'support.intro':
        'Arcana is made by one person. The ads keep it free; a tip keeps it growing, with new spreads and more depth in every card.',
    'support.owned':
        'You have the lifetime unlock: no ads, ever. Thank you, truly.',
    'support.lifetime': 'Remove ads forever · {price}',
    'support.lifetimeHint':
        'One payment, no subscription. Removes every ad for good, on this store account.',
    'support.restore': 'Restore a previous purchase',
    'support.noAds':
        'Want no ads at all? The Android app has a one-time lifetime unlock.',
    'support.coffee': '☕ Buy me a coffee',
    'support.pix': '🇧🇷 Pix (Mercado Pago)',
    'support.noLink': 'The tip link is not set up yet.',
    'support.noAccount': 'No account, no sign-in.',
    'support.about': 'About',
    'support.privacy': 'Privacy policy',
    'support.close': 'Close',

    // about
    'about.title': 'About Arcana',
    'about.intro':
        'Arcana is a tarot app with a full 78-card deck: the 22 major arcana and the four suits of the minor arcana. It offers a daily card, a love reading, a work reading, a past-present-future spread and a yes-or-no card, in English and Portuguese.',
    'about.howTitle': 'How a reading works',
    'about.how1':
        'The cards are shuffled with your device’s secure random generator, so every draw is genuinely random, and a card can appear upright or reversed.',
    'about.how2':
        'Each card’s meaning is hand-written and depends on its position in the spread and the theme: love, work, or general guidance. No artificial intelligence writes your reading, and nothing you type leaves your phone.',
    'about.how3':
        'Tarot is a tool for reflection. It does not predict the future, and it is not medical, legal, financial or psychological advice.',
    'about.creditsTitle': 'Card artwork',
    'about.credits':
        'The card images are the Rider-Waite-Smith deck, illustrated by Pamela Colman Smith in 1909 and published by William Rider & Son. The artwork is in the public domain; the scans come from Wikimedia Commons.',
    'about.faqTitle': 'Questions',
    'about.q1': 'Why can I draw the daily card only once a day?',
    'about.a1':
        'The ritual means something because it is rare. Your card stays readable until midnight on your phone’s clock, then the deck is ready again.',
    'about.q2': 'Why a video before each reading?',
    'about.a2':
        'Arcana is free, and the short video pays for it. If you would rather not watch it, the Android app has a one-time purchase that removes every ad. Your cards are drawn first, so closing the app does not change them.',
    'about.q3': 'Where are my readings stored?',
    'about.a3':
        'On your phone only. There are no accounts and no servers. Uninstalling the app erases them.',
    'about.q4': 'How do I get in touch?',
    'about.a4': 'Write to',

    // privacy
    'privacy.consent':
        'In the EU, the UK and Switzerland we ask for your consent before showing ads, through the Google consent form, in the Android app and on the website. You can change your choice at any time: in the Android app with the button below, on the website with the privacy settings button that Google shows.',
    'privacy.adChoices': 'Ad privacy choices',
    'privacy.title': 'Privacy Policy',
    'privacy.intro':
        'Arcana is a small app that respects your privacy. This policy explains what is stored, what is shared and why. By using Arcana you agree to it.',
    'privacy.deviceTitle': 'What stays on your device',
    'privacy.p1':
        'Your readings, the names you type for love readings, the birth data you enter for a birth chart (date, time and place), your language and your sound setting are stored on your device only. Arcana has no accounts and no server, so we never receive them. Uninstalling the app or clearing its data erases them. One exception: if the place you type for a birth chart is not in the built-in city list, the place text alone is sent to Photon (photon.komoot.io, OpenStreetMap data) to find it. Your date, time and name are never sent.',
    'home.journal': 'Gratitude journal',
    'journal.title': 'Gratitude journal',
    'journal.today': 'Today\'s prompt',
    'journal.placeholder': 'One line is enough',
    'journal.save': 'Save',
    'journal.saved': 'Saved for today. You can edit it until midnight.',
    'journal.streak': '{n} day streak',
    'journal.streak0': 'No streak yet',
    'journal.past': 'Past entries',
    'journal.empty': 'Nothing written yet.',
    'journal.export': 'Export',
    'journal.deleteAll': 'Delete all',
    'journal.confirmTitle': 'Delete the whole journal?',
    'journal.confirmBody': 'Every entry is erased from this device. This cannot be undone.',
    'journal.cancel': 'Cancel',
    'journal.confirm': 'Delete',
    'journal.privacy': 'Private: entries stay on this device and never leave it.',
    'journal.prompt0': 'What is something small that felt good today?',
    'journal.prompt1': 'Who are you glad to have in your life right now?',
    'journal.prompt2': 'What went better than you expected?',
    'journal.prompt3': 'What is something ordinary you would miss if it were gone?',
    'journal.prompt4': 'What did your body let you do today?',
    'journal.prompt5': 'What moment would you be happy to live again?',
    'privacy.journal': 'Your gratitude journal is stored on your device only, in the same local storage as your settings. Entries are never sent anywhere. You can export them or delete them all from the journal screen at any time.',
    'privacy.adsTitle': 'Advertising',
    'privacy.p2a':
        'Arcana is paid for by ads from Google (AdMob in the phone app, AdSense on the web). Google may use identifiers and cookies to show and measure ads, and may personalize them. You can manage this in',
    'privacy.googleSettings': 'Google ad settings',
    'privacy.p2b': 'and learn how Google uses data at',
    'privacy.p3':
        'Unless you bought the one-time ad-free unlock, a rewarded video is shown before each reading is revealed. The purchase itself is handled by Google Play; we never see your payment details. No other data is sent to us.',
    'privacy.ageTitle': 'Age',
    'privacy.age':
        'Arcana is meant for adults. It is not directed at children under 18.',
    'privacy.changesTitle': 'Changes to this policy',
    'privacy.changes':
        'If this policy changes, the new text and its date will appear on this page.',
    'privacy.contactTitle': 'Contact us',
    'privacy.contact': 'If you have any questions about this policy, write to',
    'privacy.updated': 'Last updated: {date}',

    // birth chart
    'home.hello': 'Hello, {name}',
    'home.helloAnon': 'Hello',
    'home.chart.name': 'Birth chart',
    'home.chart.desc':
        'The sky at the moment you were born: your Sun, Moon, rising sign, planets and houses.',
    'chart.title': 'Birth chart',
    'chart.intro':
        'Enter when and where you were born. The planets are placed from astronomical positions; your rising sign and houses also depend on the exact time and place.',
    'chart.name': 'Name (optional)',
    'chart.date': 'Date of birth',
    'chart.time': 'Time of birth',
    'chart.timeUnknown': 'I do not know my birth time',
    'chart.timeUnknownHint':
        'Without a birth time we cannot find your rising sign or houses. The chart is cast for noon.',
    'chart.place': 'City of birth',
    'chart.placeHint': 'Start typing a city',
    'chart.placeLoading': 'Loading cities…',
    'chart.placeSearching': 'Searching…',
    'chart.placeNone':
        'No city found. Try another spelling, or enter coordinates.',
    'chart.placeChosen': 'Time zone: {zone}',
    'chart.manual': 'Enter coordinates instead',
    'chart.manualBack': 'Search for a city instead',
    'chart.manualPlace': 'Place name (optional)',
    'chart.manualLat': 'Latitude (north positive, south negative)',
    'chart.manualLon': 'Longitude (east positive, west negative)',
    'chart.manualZone': 'Time zone (for example America/Sao_Paulo)',
    'chart.error.date': 'Enter a valid date of birth.',
    'chart.error.time':
        'Enter the time of birth, or tick that you do not know it.',
    'chart.error.place': 'Choose the city of birth from the list.',
    'chart.error.lat': 'Latitude must be a number from -90 to 90.',
    'chart.error.lon': 'Longitude must be a number from -180 to 180.',
    'chart.error.zone':
        'That time zone is not known. Use a name like Europe/Lisbon.',
    'chart.error.calc':
        'This chart could not be calculated. Check the date, time and place.',
    'chart.submit': 'Reveal my chart',
    'chart.unlockNote': 'A short video opens your chart.',
    'chart.unlockNoteFree': 'Your chart opens at once, with no ads.',
    'chart.anotherHintFree': 'For yourself or someone else.',
    'chart.dismissed':
        'The video was closed before the end, so the chart stays sealed. Try again when you are ready.',
    'chart.saved': 'Your charts',
    'chart.mine': 'My chart',
    'chart.delete': 'Delete',
    'chart.cancel': 'Cancel',
    'chart.deleteTitle': 'Delete this chart?',
    'chart.deleteBody':
        'The birth data is erased from this device. You can enter it again at any time.',
    'chart.new': 'New chart',
    'chart.notFound': 'This chart is no longer saved.',
    'chart.heading': 'Chart for {name}',
    'chart.born': 'Born {date}, {time}, in {place}',
    'chart.bornNoTime': 'Born {date} in {place}, time unknown',
    'chart.zone': 'Local time was UTC{offset} ({zone})',
    'chart.wheel': 'Birth chart wheel',
    'chart.bigThree': 'Your big three',
    'chart.another': 'Calculate another chart',
    'chart.anotherHint':
        'For yourself or someone else. Each new chart takes a short video.',
    'chart.sun': 'Sun',
    'chart.moon': 'Moon',
    'chart.rising': 'Rising',
    'chart.ruler': 'Your chart ruler',
    'chart.placements': 'Planets and points',
    'chart.angles': 'Angles',
    'chart.house': 'House {n}',
    'chart.retrograde': 'retrograde',
    'chart.aspects': 'Aspects',
    'chart.system.placidus': 'Placidus houses',
    'chart.system.whole-sign': 'Whole-sign houses',
    'chart.warn.noTime':
        'No birth time: the chart is cast for noon and has no rising sign, angles or houses. The Moon moves about 13 degrees a day, so its position is approximate.',
    'chart.warn.ambiguous-time':
        'The clocks went back at this hour, so this local time happened twice. We used the first one. If you were born the second time, the rising sign will differ.',
    'chart.warn.skipped-time':
        'The clocks jumped forward over this hour, so this local time never happened. We read it as the hour before the change.',
    'chart.warn.polar-fallback':
        'Placidus houses do not exist this close to the pole. Whole-sign houses are used instead.',
    'chart.warn.moon-sign-uncertain':
        'The Moon changed sign during this day, so its sign depends on the birth time.',
    'chart.accuracy':
        'The planets are calculated from astronomical positions, to within a fraction of a degree. The rising sign moves about one degree every four minutes, so a birth time that is off by a few minutes can change the degree, and sometimes the sign.',
    // sleep sounds
    'home.sleep.name': 'Sleep sounds',
    'home.sleep.desc':
        'Rain, brown noise and ocean, made on your device, with a sleep timer.',
    'sleep.title': 'Sleep sounds',
    'sleep.intro':
        'Let the day settle. Slide to set the timer, then choose a sound to open the portal.',
    'sleep.rain': 'Rain',
    'sleep.brown': 'Brown noise',
    'sleep.ocean': 'Ocean',
    'sleep.timer': 'Sleep timer',
    'sleep.minutes': '{n} min',
    'sleep.hours': '{n} h',
    'sleep.hoursMinutes': '{h} h {m} min',
    'sleep.remaining': 'Fades out in {time}',
    'sleep.stop': 'Stop',
    'sleep.declined': 'Watch the whole video to open the sleep sounds.',
    'sleep.note':
        'A short video opens the sounds for this session. Keep the app open and the screen on while you listen.',
    'sleep.noteFree':
        'No ads. Keep the app open and the screen on while you listen.',
    'sleep.noteBackground':
        'A short video opens the sounds for this session. The sound keeps playing with the screen off; stop it here or from the notification.',
    'sleep.noteBackgroundFree':
        'No ads. The sound keeps playing with the screen off; stop it here or from the notification.',
    'about.creditsChart':
        'Birth charts use planetary positions from Astronomy Engine (Don Cross, MIT license), city coordinates and time zones from GeoNames (geonames.org, CC BY 4.0), the IANA time zone database built into your device, and, for places outside the built-in list, Photon with © OpenStreetMap contributors (ODbL).',
};

export type MessageKey = keyof typeof enUS;
